import { Sync } from '@mui/icons-material';
import {
  AlertColor,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import strings from 'common/Translation/Translate';
import { FormDataModel } from 'components/Form';
import PageLoader from 'components/PageLoader';
import StyledTabs from 'components/StyledTabs';
import Notification from 'components/Notification';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ListPage from 'screens/Authenticated/CommonPage';
import MasterDataEntityDrawer from 'screens/Authenticated/CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from 'screens/Authenticated/CommonPage/lastUpdatedColumn';
import { usageTableColumn } from 'screens/Authenticated/CommonPage/usageColumn';
import axiosInstance from 'utils/axios';
import { hotelogix, PageProps, ycs } from 'utils/constants/data';
import { privileges } from 'utils/constants/privileges';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { Privilege, languages, selectIntegration } from 'utils/redux/reducer/authentication-slice';
import { pageTitleSx } from 'utils/styles/pageHeader';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { EditAddOnCategoryForm } from './EditAddOnCategories/EditAddOnCategoryForm';

const MAIN_TAB = 0;
const YCS_TAB = 1;

const AddOnCategoriesList: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];
  const integration = useSelector(selectIntegration);
  const languageList = useSelector(languages) as { text: string; value: string }[];

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({ display: false, severity: null, message: '' });

  const [activeTab, setActiveTab] = useState(MAIN_TAB);
  const [ycsRows, setYcsRows] = useState<any[]>([]);
  const [ycsLoading, setYcsLoading] = useState(false);
  const [ycsError, setYcsError] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});

  const isYcsIntegration = (integration || '').toUpperCase() === ycs;
  const isHotelogix = integration === hotelogix;
  const syncUrl = isYcsIntegration ? URLS.ycsSyncAddOnCategories : URLS.syncAddOnCategories;

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: (error.response.data?.error_description ?? error.response.data.message) ?? error.message,
    });
  };

  const formModel = useMemo(() => {
    const baseFields = EditAddOnCategoryForm(strings, editingId as any)
      .filter(item => item.field !== 'name')
      .map(field => ({ ...field, responsive: { xs: 12 } }));
    const nameField = EditAddOnCategoryForm(strings, editingId as any)[3];
    const nameFields = (languageList || []).map((language, indexLang) => {
      const field = { ...nameField };
      field.field = `name_${language.value}`;
      field.autoFocus = indexLang === 0;
      field.label = `${field.label} (${language.text})`;
      field.responsive = { xs: 12 };
      return field;
    });
    return [...baseFields, ...nameFields];
  }, [languageList, editingId]);

  const openDrawer = useCallback(
    async (recordId: number | null) => {
      setEditingId(recordId);
      if (recordId) {
        try {
          setDrawerLoading(true);
          const { data } = await axiosInstance.get(URLS.getAddOnCategoriesById(recordId));
          const container: FormDataModel = {
            code: data?.code ?? '',
            serviceChargeApplicable: data?.serviceChargeApplicable ?? data?.isServiceChargeApplicable ?? false,
          };
          (languageList || []).forEach(language => {
            container[`name_${language.value}`] = data?.localizedAttributes?.[language.value]?.name ?? '';
          });
          setFormValues(container);
        } catch (error: any) {
          handleCatch(error);
          return;
        } finally {
          setDrawerLoading(false);
        }
      } else {
        const container: FormDataModel = { code: '', serviceChargeApplicable: false };
        (languageList || []).forEach(language => {
          container[`name_${language.value}`] = '';
        });
        setFormValues(container);
      }
      setDrawerOpen(true);
    },
    [languageList],
  );

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSaveAddOnCategory = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const localizedAttributes = (languageList || []).map(item => ({
        language: item.value,
        name: body[`name_${item.value}`],
      }));
      const payload = {
        code: body.code,
        isServiceChargeApplicable: body.serviceChargeApplicable ?? false,
        localizedAttributes,
      };
      if (editingId) {
        await axiosInstance.put(URLS.getAddOnCategoriesById(editingId), payload);
      } else {
        await axiosInstance.post(URLS.addAddOnCategories, payload);
      }
      setMessage({
        display: true,
        severity: 'success',
        message: editingId ? strings.common_update_message : strings.common_save_message,
      });
      closeDrawer();
      getAddOnCategoryList(null as any);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = {
    title: strings.menu_text_addOnCategories,
    subtitle: strings.addOnCategory_list_subtitle,
    searchField: 'name',
    searchPlaceholder: 'Search add-on categories',
    AddButtonText: strings.menu_text_addOnCategories,
    listApi: URLS.getPaginatedAddOnCategories,
    addPrivilege: privileges.add_add_on_categories,
    editPrivilege: privileges.edit_add_on_categories,
    customAdd: () => openDrawer(null),
    customEdit: (id: number) => openDrawer(id),
    tableColumn: [
      { field: 'code', headerName: strings.addOnCategoryCode_column_name, flex: 2, sortable: true },
      {
        field: 'name',
        headerName: strings.addOnCategoryName_column_name,
        flex: 3,
        sortable: true,
        localField: 'localizedAttributes.name',
      },
      usageTableColumn(row => [
        {
          count: row.addOnCount,
          singular: 'Add-on',
          plural: 'Add-ons',
          onClick: () =>
            navigate(`${routes.addOns}?addOnCategory=${encodeURIComponent(row.code)}`),
        },
      ]),
      lastUpdatedTableColumn(),
    ],
  };

  const getAddOnCategoryList = useCallback(
    async (filterData: PageProps) => {
      try {
        setIsLoading(true);
        const requestBody = filterData || { page: index + 1, size: pageSize, filter: filter, sort: ['code:ASC'] };
        const { data } = await axiosInstance.post(pageConfig.listApi, requestBody);
        setPageResponse(data);
        setIsLoading(false);
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setIsLoading(false);
      }
    },
    [pageConfig.listApi, index, pageSize, filter],
  );

  const fetchYcsAddOnCategories = useCallback(async () => {
    try {
      setYcsLoading(true);
      setYcsError('');
      const { data } = await axiosInstance.get(URLS.ycsAddOnCategories);
      setYcsRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setYcsRows([]);
      setYcsError(e?.response?.data?.message || e?.message || strings.something_went_wrong);
    } finally {
      setYcsLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getAddOnCategoryList(null as any);
    })();
  }, [getAddOnCategoryList]);

  useEffect(() => {
    if (activeTab === YCS_TAB) {
      fetchYcsAddOnCategories();
    }
  }, [activeTab, fetchYcsAddOnCategories]);

  const tabs = useMemo(() => {
    const base = [{ label: strings.menu_text_addOnCategories, value: MAIN_TAB }];
    if (isYcsIntegration) {
      base.push({ label: strings.ycs_addOnCategories_tab_label, value: YCS_TAB });
    }
    return base;
  }, [isYcsIntegration]);

  useEffect(() => {
    if (!isYcsIntegration && activeTab === YCS_TAB) {
      setActiveTab(MAIN_TAB);
    }
  }, [isYcsIntegration, activeTab]);

  const syncAddOnCategories = async () => {
    setIsLoading(true);
    try {
      const { status } = await axiosInstance.post(syncUrl);
      if (status === 200 || status === 201) {
        setIsLoading(false);
        await getAddOnCategoryList(null as any);
      }
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderYcsTable = () => {
    if (ycsLoading) {
      return <PageLoader />;
    }
    if (ycsError) {
      return (
        <Typography
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 1,
            bgcolor: theme.palette.error.light + '20',
            color: theme.palette.error.main,
            border: `1px solid ${theme.palette.error.light}`,
          }}>
          {ycsError}
        </Typography>
      );
    }
    if (!ycsRows.length) {
      return (
        <Typography
          sx={{
            p: 2,
            mt: 3,
            borderRadius: 1,
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          }}
          variant="h6">
          {strings.no_data_available}
        </Typography>
      );
    }
    return (
      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          backgroundColor: theme.palette.background.paper,
          mt: 2.5,
        }}>
        <TableContainer sx={{ borderRadius: 2, boxShadow: 'none' }} component={Paper}>
          <Table stickyHeader>
            <TableHead
              sx={{
                '.MuiTableCell-root': {
                  backgroundColor: theme.palette.grey[50],
                  color: theme.palette.text.primary,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  padding: '12px 16px',
                },
              }}>
              <TableRow>
                <TableCell>{strings.ycs_column_ref_id}</TableCell>
                <TableCell>{strings.addOnCategoryName_column_name}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  padding: '8px 16px',
                  fontSize: '0.875rem',
                },
              }}>
              {ycsRows.map((row: any, idx: number) => (
                <TableRow key={row?.refId ?? idx}>
                  <TableCell>{row?.refId ?? '-'}</TableCell>
                  <TableCell>{row?.name ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const tabHeader = tabs.length > 1 && (
    <Box sx={{ mb: 2 }}>
      <StyledTabs
        tabs={tabs}
        value={activeTab}
        onChange={(_: any, value: number) => setActiveTab(value)}
      />
    </Box>
  );

  if (activeTab === YCS_TAB && isYcsIntegration) {
    return (
      <>
        <Typography sx={{ ...pageTitleSx, mb: 1 }}>{strings.menu_text_addOnCategories}</Typography>
        {tabHeader}
        {renderYcsTable()}
        {message.display && (
          <Notification
            isOpen={message.display}
            message={message.message}
            severity={message.severity as AlertColor}
            closeNotification={closeNotification}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {tabHeader}
      <div style={{ textAlign: 'right' }}>
        {userPrivileges?.includes(privileges.add_add_on_categories) && (isHotelogix || isYcsIntegration) && (
          <Button disabled={isLoading} variant="text" onClick={syncAddOnCategories}>
            <Sync />
          </Button>
        )}
      </div>
      <ListPage
        pageConfig={pageConfig}
        pageResponse={pageResponse}
        getList={getAddOnCategoryList}
        isLoading={isLoading}
      />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.addOnCategory_title_edit : strings.addOnCategory_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        onClose={closeDrawer}
        onSave={handleSaveAddOnCategory}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default AddOnCategoriesList;
